import debounce from "@mui/material/utils/debounce";
import { useAsync } from "@react-hookz/web";
import { useMemo } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";

import type { hooksDebouncedAutoCompleteQuery } from "scmp-app/queries/__generated__/hooksDebouncedAutoCompleteQuery.graphql";

export function useDebouncedAutoComplete() {
  const environment = useRelayEnvironment();

  const autoCompleteQuery = graphql`
    query hooksDebouncedAutoCompleteQuery($query: String!) {
      autoCompleteSearch(query: $query) {
        suggestions
      }
    }
  `;

  const [state, actions] = useAsync(async (input: null | string): Promise<string[]> => {
    if (!input) return [];
    try {
      const result = await fetchQuery<hooksDebouncedAutoCompleteQuery>(
        environment,
        autoCompleteQuery,
        { query: input },
      ).toPromise();
      return result?.autoCompleteSearch?.suggestions
        ? [...result.autoCompleteSearch.suggestions]
        : [];
    } catch {
      return [];
    }
  }, []);

  const debouncedFetchSuggestions = useMemo(
    () => debounce(actions.execute, 400),
    [actions.execute],
  );

  const resetSuggestions = () => {
    void actions.execute("");
  };

  return {
    fetchSuggestions: debouncedFetchSuggestions,
    resetSuggestions,
    suggestionsState: state ?? [],
  };
}
